<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import ConditionRow from './ConditionRow.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { filterTypes } = useConversationFilterContext();

const { t } = useI18n();
const store = useStore();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);
const customerMessageTimeFilter = ref(store.getters.getChatCustomerMessageTimeFilter || '');

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
  customerMessageTimeFilter.value = '';
  store.dispatch('setChatCustomerMessageTimeFilter', '');
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

// Handler para cuando cambia el SelectMenu
const handleCustomerMessageTimeChange = (newValue) => {
  customerMessageTimeFilter.value = newValue;
  store.dispatch('setChatCustomerMessageTimeFilter', newValue || '');
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    // Leer del store que se actualizó cuando cambió el selector
    const currentValue = store.getters['getChatCustomerMessageTimeFilter'];
    emit('updateFolder', filters.value, folderNameLocal.value, currentValue);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  
  // Aplicar el filtro de tiempo del último mensaje del cliente si está seleccionado
  if (customerMessageTimeFilter.value) {
    store.dispatch('setChatCustomerMessageTimeFilter', customerMessageTimeFilter.value);
  } else {
    store.dispatch('setChatCustomerMessageTimeFilter', '');
  }
  
  emit('applyFilter', filters.value, customerMessageTimeFilter.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

const customerMessageTimeOptions = computed(() => [
  {
    label: t('CHAT_LIST.CUSTOMER_MESSAGE_TIME_FILTER.all.TEXT'),
    value: '',
  },
  {
    label: t('CHAT_LIST.CUSTOMER_MESSAGE_TIME_FILTER.24h.TEXT'),
    value: '24h',
  },
  {
    label: t('CHAT_LIST.CUSTOMER_MESSAGE_TIME_FILTER.24_72h.TEXT'),
    value: '24_72h',
  },
]);

const activeCustomerMessageTimeLabel = computed(
  () =>
    customerMessageTimeOptions.value.find(m => m.value === customerMessageTimeFilter.value)?.label ||
    customerMessageTimeOptions.value[0].label
);

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div class="border-b border-n-weak pb-4">
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ $t('CHAT_LIST.CHAT_SORT.CUSTOMER_LAST_MESSAGE') }}
        </span>
        <SelectMenu
          v-model="customerMessageTimeFilter"
          :options="customerMessageTimeOptions"
          :label="activeCustomerMessageTimeLabel"
          sub-menu-position="left"
          @update:model-value="handleCustomerMessageTimeChange"
        />
      </div>
    </div>
    <div v-if="props.isFolderView">
      <label class="border-b border-n-weak pb-6">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ t('FILTER.FOLDER_LABEL') }}
        </div>
        <input
          v-model="folderNameLocal"
          class="py-1.5 px-3 text-n-slate-12 bg-n-alpha-1 text-sm rounded-lg reset-base w-full"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </label>
    </div>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
